import { CategoryTypeEnum, UiCategoryExt } from '../../types';

import { Item, PanelData, PanelMap } from './types';

export const ROOT_PATH = '/shop';

const BOLD_LINKS_COUNT = 3;

function isBold(parentLevel: number, index: number, type?: CategoryTypeEnum) {
    // display first 3 links in Panel1 (level2) and Panel2 (level3)
    // when the category type is not of Brands
    return (
        type !== CategoryTypeEnum.BRANDS &&
        parentLevel !== 3 &&
        index < BOLD_LINKS_COUNT
    );
}

function isSeperator(
    parentLevel: number,
    index: number,
    type?: CategoryTypeEnum,
) {
    // make the last bold link in Panel2 (level3) to be a seperator
    // when the category type is not of Brands
    return (
        type !== CategoryTypeEnum.BRANDS &&
        parentLevel !== 3 &&
        index === BOLD_LINKS_COUNT - 1
    );
}

function toItem(category: UiCategoryExt, special: boolean): Item {
    const {
        animalType,
        description,
        extId,
        level,
        path,
        title,
        type,
        children,
        specials,
        highlighted,
        active,
    } = category;

    const hasChildren = !!(children?.length || specials?.length);

    return {
        animalType,
        description,
        extId,
        level,
        path,
        title,
        type,
        highlighted,
        active: level !== 2 && active,
        hasChildren,
        isSpecial: special,
    };
}

function convertTrackingIdCategory(type: string): string {
    switch (type) {
        case CategoryTypeEnum.ANIMAL:
            return `-animal`;
        case CategoryTypeEnum.BRANDS:
            return `-topBrands`;
        case CategoryTypeEnum.OFFERS:
            return `-specialOffers`;
        default:
            return '';
    }
}

function convertTrackingIdTitle(title: string): string {
    return title
        .split(' ')
        .map((substr) => substr.replace(' ', '-'))
        .join('-');
}

export function convertTrackingId(
    category: UiCategoryExt,
    parentPanel: PanelData,
    index?: number,
): string | undefined {
    if (category.level === 4) return undefined;

    let trackingId = 'header-category-mobile-menu';

    if (category.level === 3) {
        if (parentPanel.currentType) {
            trackingId += convertTrackingIdCategory(parentPanel.currentType);
        }
        trackingId += '-level4-links';
        if (parentPanel.currentTitle) {
            trackingId += `-${convertTrackingIdTitle(
                parentPanel.currentTitle,
            )}`;
        }
        if (index !== undefined) {
            trackingId += `-${index}`;
        }
        trackingId += `-${convertTrackingIdTitle(category.title)}`;
    }

    if (category.level === 2) {
        if (category.type) {
            trackingId += convertTrackingIdCategory(category.type);
        }
        trackingId += '-level3-links';
        trackingId += `-${convertTrackingIdTitle(category.title)}`;
    }

    return trackingId;
}

function addCategoryPanels(
    category: UiCategoryExt,
    parentPanel: PanelData,
    panelMap: PanelMap,
    allProductsText?: string,
    index?: number,
): void {
    const {
        animalType,
        description,
        extId,
        level,
        path,
        title,
        type,
        children = [],
        specials = [],
        highlighted,
    } = category;

    const panelItems = [];

    if (allProductsText) {
        const parent: UiCategoryExt = {
            animalType,
            description,
            extId,
            level,
            path,
            title: `${allProductsText} ${title}`,
            type,
            highlighted,
            active: false,
            children: [],
            specials: [],
        };

        panelItems.push({ ...toItem(parent, false), isParent: true });
    }

    const childItems = children?.map((childCategory, index) => {
        return {
            ...toItem(childCategory, false),
            isBold: isBold(level, index, type),
            isSeperator: isSeperator(level, index, type),
            isParent: false,
        };
    });
    const specialItems = specials?.map((specialChildCategory, index) => {
        return {
            ...toItem(specialChildCategory, true),
            isBold: isBold(level, childItems.length + index, type),
            isSeperator: isSeperator(level, childItems.length + index, type),
            isParent: false,
        };
    });

    const { currentTitle: parentTitle, currentPath: parentPath } = parentPanel;

    const panel = {
        show: false,
        items: panelItems.concat(childItems, specialItems),
        currentTitle: title,
        currentPath: path,
        currentType: type,
        parentTitle,
        parentPath,
        trackingId: convertTrackingId(category, parentPanel, index),
    };
    panelMap.set(path, panel);

    children?.forEach((childCategory, childIndex) =>
        addCategoryPanels(
            childCategory,
            panel,
            panelMap,
            allProductsText,
            childIndex,
        ),
    );
    specials?.forEach((specialChildCategory, specialChildIndex) =>
        addCategoryPanels(
            specialChildCategory,
            panel,
            panelMap,
            allProductsText,
            specialChildIndex,
        ),
    );
}

export function convertPanelMap(
    categories: UiCategoryExt[],
    allProductsText?: string,
): PanelMap {
    const panelMap = new Map<string, PanelData>();

    const rootPanel = {
        currentPath: ROOT_PATH,
        show: true,
        items: categories.map((category, index) => {
            return {
                ...toItem(category, false),
                isBold: index < BOLD_LINKS_COUNT,
                isParent: false,
            };
        }),
    };

    panelMap.set(ROOT_PATH, rootPanel);

    categories.forEach((category) =>
        addCategoryPanels(category, rootPanel, panelMap, allProductsText),
    );

    return panelMap;
}
